/**
 * Add Organization Address Form Composer
 *
 * Form for creating new addresses for an organization.
 * addressId is auto-generated on submission.
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import { useToast } from '@/contexts/ToastContext'
import { PhoneInputField } from '@/components/ui/phone-input'
import { CountryDropdown, G20_COUNTRIES } from '@/components/ui/country-dropdown'
import {
  AddOrganizationAddressFormSchema,
  transformToApiRequest,
  validateField,
  FORM_CONFIG,
  type AddOrganizationAddressFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderButtonControl,
  type ControlConfig,
} from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddOrganizationAddressComposerProps = {
  organizationId: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddOrganizationAddressFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddOrganizationAddressComposer(props: AddOrganizationAddressComposerProps) {
  const { organizationId, onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props
  const { toast } = useToast()

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['addressName', 'addressType', 'street', 'city', 'state', 'postalCode', 'country'].includes(name)
  }

  const upsertAddress = useApiMutation('upsertOrganizationAddress', 'POST', {
    invalidates: ['getOrganization'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create address:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddOrganizationAddressFormSchema,
    defaultValues: {
      addressName: initialValues?.addressName ?? '',
      addressType: initialValues?.addressType ?? '',
      phone: initialValues?.phone ?? '',
      street: initialValues?.street ?? '',
      city: initialValues?.city ?? '',
      state: initialValues?.state ?? '',
      postalCode: initialValues?.postalCode ?? '',
      country: initialValues?.country ?? '',
      id: initialValues?.id,
    },
    onSubmit: async ({ value }: { value: any }) => {
      const parsed = AddOrganizationAddressFormSchema.safeParse({
        addressName: (value.addressName as string)?.trim() ?? '',
        addressType: value.addressType,
        phone: (value.phone as string)?.trim() ?? '',
        street: (value.street as string)?.trim() ?? '',
        city: (value.city as string)?.trim() ?? '',
        state: (value.state as string)?.trim() ?? '',
        postalCode: (value.postalCode as string)?.trim() ?? '',
        country: (value.country as string)?.trim() ?? '',
        id: value.id as string | undefined,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        // Surface WHY the form didn't submit instead of returning silently.
        const first = parsed.error.issues[0]
        toast({
          title: 'Cannot save address',
          description: first
            ? `${String(first.path[0] ?? 'Field')}: ${first.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        return
      }

      const apiRequest = transformToApiRequest(parsed.data, organizationId)
      await upsertAddress.mutateAsync(apiRequest)
    },
  })

  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddOrganizationAddressFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const baseConfig: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }

          if (control.htmlControl === 'phone') {
            return (
              <PhoneInputField
                label={control.label}
                value={(bound.state.value as string) ?? ''}
                onChange={(val) => bound.handleChange(val ?? '')}
                required={isRequired}
                disabled={control.disabled ?? false}
                error={bound.state.meta.errors?.[0] as string | undefined}
              />
            )
          }

          if (control.htmlControl === 'country') {
            return (
              <CountryDropdown
                label={control.label}
                value={(bound.state.value as string) ?? ''}
                onChange={(country) => bound.handleChange(country?.name ?? '')}
                options={G20_COUNTRIES}
                required={isRequired}
                disabled={control.disabled ?? false}
                error={bound.state.meta.errors?.[0] as string | undefined}
              />
            )
          }

          if (control.htmlControl === 'select') {
            return renderSelectControl(
              {
                ...baseConfig,
                options: control.selectOptions || [],
              },
              bound,
            )
          }

          return renderTextControl(baseConfig, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}

          {renderButtonControl(
            {
              label: upsertAddress.isPending
                ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
                : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
              variant: 'default',
              type: 'submit',
            },
            { SubmitButton: form.SubmitButton },
          )}
        </div>
      </div>
    </form.AppForm>
  )
}

export {
  AddOrganizationAddressFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddOrganizationAddressFormData,
} from './schema'
